import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RequestStatus, GenericError, JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type { SliceFactoryPropsWithMessages } from 'src/store/shared/types';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type { JsonReportSuccess } from 'src/store/shared/reports/types';
import { ReportCommands } from 'src/store/shared/reports/types';
import { cancelReport, confirmReport, requestReport } from 'src/store/shared/reports/actions';

registerErrorMessage(ReportCommands.GetReport, {
  title: 'Report Download Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on report send. ${json.message}`,
});

export interface ReportsState {
  requestStatus: RequestStatus;
  error: GenericError | null;

  reportPreview: string | null;
  reportId: number | null;
}

export function initialReportSlice({
  sliceName = 'reports',
  prefix,
  commands = ReportCommands,
  initialState,
  extraReducers,
  messages,
}: SliceFactoryPropsWithMessages<ReportsState, typeof ReportCommands>) {
  const allowedCommands = Object.values(commands);

  if (!initialState) {
    throw new Error('Invalid initial state for reports slice');
  }

  return createSlice({
    name: `${prefix}/${sliceName}`,
    initialState,
    reducers: {
      resetPreview: (state) => {
        state.reportPreview = null;
        state.requestStatus = null;
      },
    },
    extraReducers: (builder) => {
      if (typeof extraReducers === 'function') {
        extraReducers(builder);
      }

      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error } = parseResponse(action.payload, allowedCommands);

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/${sliceName}/processing ${actionOnMessage.toString()}`,
          action,
          allowedCommands,
        );

        const { command, okCommand } = json;

        if (error) {
          state.error = error;
          state.requestStatus = 'error';
          return;
        }

        if (isOkCommand(command)) {
          const { id, result } = json as JsonReportSuccess;
          switch (okCommand) {
            case ReportCommands.GetReport:
              if (id && result) {
                state.reportPreview = result;
                state.reportId = id;
              } else {
                state.requestStatus = 'success';
              }
              break;
            case ReportCommands.SendReport:
              state.requestStatus = 'success';
              state.reportId = null;
              state.reportPreview = null;
              break;
            default:
          }
        }
      });
    },
  });
}

/**
 * Creates standard extra reducers for reports slice
 * @param requestReportAction - The async thunk for requesting reports
 * @param confirmReportAction - The async thunk for confirming reports
 * @param cancelReportAction - The async thunk for canceling reports
 */
export function createReportsExtraReducers(
  requestReportAction: typeof requestReport,
  confirmReportAction: typeof confirmReport,
  cancelReportAction: typeof cancelReport,
) {
  return (builder: ActionReducerMapBuilder<ReportsState>) => {
    builder.addCase(requestReportAction.pending, (state) => {
      state.requestStatus = 'pending';
    });

    builder.addCase(requestReportAction.rejected, (state) => {
      state.requestStatus = 'error';
    });

    builder.addCase(cancelReportAction.fulfilled, (state) => {
      state.reportId = null;
      state.reportPreview = null;
      state.requestStatus = null;
    });

    builder.addCase(confirmReportAction.fulfilled, (state) => {
      state.reportId = null;
      state.reportPreview = null;
    });
  };
}
